import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Sparkles, Upload, Save, Send, Eye, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/apiClient";
import { AdminHeader, AdminPage } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATS = ["Press Release", "Media Statement", "Institutional Update", "Important Development"];
const EMPTY = { headline: "", subheadline: "", excerpt: "", body: "", category: "Press Release",
  publish_date: "", author: "", related_pils: [], tags: [], seo_title: "", meta_description: "",
  social_excerpt: "", state: "draft", featured: false };

function Field({ label, children, hint }) {
  return <div className="space-y-1.5"><label className="label-eyebrow text-slate-500 block">{label}</label>{children}{hint && <p className="text-xs text-slate-400">{hint}</p>}</div>;
}

export default function PressEditor() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [raw, setRaw] = useState("");
  const [pils, setPils] = useState([]);
  const fileRef = useRef();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { api.get("/pils?include_unpublished=true&limit=200").then(({ data }) => setPils(data.items)).catch(() => {}); }, []);
  useEffect(() => {
    if (isNew) return;
    api.get(`/press/${id}`).then(({ data }) => setForm({ ...EMPTY, ...data.press })).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, [id, isNew]);

  const applyAnalysis = (a) => {
    if (!a || a._ai_error) return toast.error(a?._ai_error || "AI failed");
    setForm((f) => ({ ...f, headline: a.headline || f.headline, subheadline: a.subheadline || f.subheadline,
      excerpt: a.excerpt || f.excerpt, body: a.body || f.body, category: a.category || f.category,
      tags: a.tags || f.tags, seo_title: a.seo_title || f.seo_title, meta_description: a.meta_description || f.meta_description,
      social_excerpt: a.social_excerpt || f.social_excerpt }));
    toast.success("AI draft ready — review before publishing");
  };

  const aiFromText = async () => {
    if (!raw.trim()) return toast.error("Paste some content first");
    setAiBusy(true);
    try { const { data } = await api.post("/ai/analyse-press-text", { text: raw }); applyAnalysis(data.analysis); }
    catch (e) { toast.error(e.response?.data?.detail || "AI failed"); } finally { setAiBusy(false); }
  };
  const aiFromFile = async (file) => {
    if (!file) return;
    setAiBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/ai/analyse-press", fd); applyAnalysis(data.analysis); }
    catch (e) { toast.error(e.response?.data?.detail || "AI failed"); } finally { setAiBusy(false); }
  };
  const aiRewrite = async () => {
    if (!form.body.trim()) return toast.error("Nothing to rewrite");
    setAiBusy(true);
    try { const { data } = await api.post("/ai/analyse-press-text", { text: form.body }); applyAnalysis(data.analysis); }
    catch { toast.error("AI failed"); } finally { setAiBusy(false); }
  };

  const save = async (state) => {
    if (!form.headline) return toast.error("Headline is required");
    setSaving(true);
    const payload = { ...form, state };
    try {
      let saved;
      if (isNew) { const { data } = await api.post("/press", payload); saved = data; }
      else { const { data } = await api.put(`/press/${id}`, payload); saved = data; }
      toast.success(state === "published" ? "Published" : "Saved");
      if (isNew) navigate(`/admin/press/${saved.id}`); else setForm({ ...EMPTY, ...saved });
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-slate-400 animate-pulse">Loading…</div>;

  return (
    <>
      <AdminHeader title={isNew ? "New Press Release" : "Edit Press Release"} actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild><Link to="/admin/press"><ArrowLeft size={15} className="mr-1.5" /> Back</Link></Button>
          {form.state === "published" && !isNew && <Button variant="outline" asChild><a href={`/media/${form.slug}`} target="_blank" rel="noopener noreferrer"><Eye size={15} className="mr-1.5" /> View</a></Button>}
          <Button variant="outline" onClick={aiRewrite} disabled={aiBusy}><Wand2 size={15} className="mr-1.5" /> AI Rewrite</Button>
          <Button variant="outline" onClick={() => save("draft")} disabled={saving} data-testid="press-save-draft"><Save size={15} className="mr-1.5" /> Save Draft</Button>
          <Button onClick={() => save("published")} disabled={saving} className="bg-npst-navy text-white" data-testid="press-publish"><Send size={15} className="mr-1.5" /> Publish</Button>
        </div>
      } />
      <AdminPage>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {isNew && (
              <div className="bg-white border border-slate-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-3"><Sparkles size={18} className="text-npst-gold" /><h3 className="font-serif text-xl">AI Draft from Content</h3></div>
                <Textarea rows={4} value={raw} onChange={(e) => setRaw(e.target.value)} placeholder="Paste rough notes, a statement, or content to format…" data-testid="press-raw-input" />
                <div className="flex gap-2 mt-3">
                  <Button onClick={aiFromText} disabled={aiBusy} className="bg-npst-navy text-white" data-testid="press-ai-generate">{aiBusy ? "Analysing…" : "Generate Draft"}</Button>
                  <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={aiBusy}><Upload size={15} className="mr-1.5" /> Upload Word/PDF</Button>
                  <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => aiFromFile(e.target.files[0])} />
                </div>
              </div>
            )}
            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
              <Field label="Headline"><Textarea rows={2} value={form.headline} onChange={(e) => set("headline", e.target.value)} data-testid="press-headline-input" /></Field>
              <Field label="Subheadline"><Input value={form.subheadline} onChange={(e) => set("subheadline", e.target.value)} /></Field>
              <Field label="Excerpt"><Textarea rows={2} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} /></Field>
              <Field label="Body" hint="Separate paragraphs with a blank line"><Textarea rows={12} value={form.body} onChange={(e) => set("body", e.target.value)} data-testid="press-body-input" /></Field>
            </div>
          </div>
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
              <Field label="Category"><Select value={form.category} onValueChange={(v) => set("category", v)}><SelectTrigger data-testid="press-category"><SelectValue /></SelectTrigger><SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></Field>
              <Field label="Publish Date"><Input type="date" value={(form.publish_date || "").slice(0, 10)} onChange={(e) => set("publish_date", e.target.value)} /></Field>
              <Field label="Author / Source"><Input value={form.author} onChange={(e) => set("author", e.target.value)} /></Field>
              <Field label="Related PIL"><Select value={form.related_pils?.[0] || "__none"} onValueChange={(v) => set("related_pils", v === "__none" ? [] : [v])}><SelectTrigger data-testid="press-related-pil"><SelectValue placeholder="None" /></SelectTrigger><SelectContent><SelectItem value="__none">None</SelectItem>{pils.map((p) => <SelectItem key={p.id} value={p.id}>{p.pil_code} · {p.title.slice(0, 40)}</SelectItem>)}</SelectContent></Select></Field>
              <Field label="Feature on Homepage"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured</label></Field>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
              <div className="label-eyebrow text-npst-gold">SEO & Social</div>
              <Field label="SEO Title"><Input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} /></Field>
              <Field label="Meta Description"><Textarea rows={2} value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)} /></Field>
              <Field label="Social Excerpt"><Textarea rows={2} value={form.social_excerpt} onChange={(e) => set("social_excerpt", e.target.value)} /></Field>
            </div>
          </div>
        </div>
      </AdminPage>
    </>
  );
}
